import moment from "moment";
import { HoraireHebdoDto } from "../dto/GenApi.iNotPaie.Dto/HoraireHebdoDto";
import { HoraireJourneeDto } from "../dto/GenApi.iNotPaie.Dto/HoraireJourneeDto";
import type {
	ChampHoraireJournee,
	DefinitionCreneau,
	DefinitionJour,
	ErreursValidationJournee
} from "../shared/interfaces/horaires.interface";

// Configuration des 6 jours (index 0 = lundi … 5 = samedi)
const JOURS_SEMAINE: DefinitionJour[] = [
	{ libelle: "lundi", propriete: "HoraireJourneeLundi", actifParDefaut: true },
	{ libelle: "mardi", propriete: "HoraireJourneeMardi", actifParDefaut: true },
	{ libelle: "mercredi", propriete: "HoraireJourneeMercredi", actifParDefaut: true },
	{ libelle: "jeudi", propriete: "HoraireJourneeJeudi", actifParDefaut: true },
	{ libelle: "vendredi", propriete: "HoraireJourneeVendredi", actifParDefaut: true },
	{ libelle: "samedi", propriete: "HoraireJourneeSamedi", actifParDefaut: false }
];

// Jours actifs par défaut (lundi → samedi)
export const JOURS_ACTIFS_PAR_DEFAUT: boolean[] = JOURS_SEMAINE.map(j => j.actifParDefaut);

// Champs horaires (matin/après-midi)
export const CHAMPS_HORAIRE: ChampHoraireJournee[] = ["MatinDebut", "MatinFin", "ApremDebut", "ApremFin"];

// Créneaux de la journée (matin/après-midi)
const CRENEAUX_JOURNEE: DefinitionCreneau[] = [
	{ libelle: "Matin", champDebut: "MatinDebut", champFin: "MatinFin", suffixeDebut: "matin-debut", suffixeFin: "matin-fin" },
	{ libelle: "Après-midi", champDebut: "ApremDebut", champFin: "ApremFin", suffixeDebut: "aprem-debut", suffixeFin: "aprem-fin" }
];

/**
 * Convertit une durée HH:mm en minutes.
 * @param hhMm HH:mm
 * @returns Minutes
 */
function parseHhMmEnMinutes(hhMm: string): number {
	const [h, m] = hhMm.split(":").map(v => parseInt(v, 10) || 0);

	return h * 60 + m;
}

/**
 * Date de référence pour stocker les heures (seules HH:mm comptent).
 * Cette date est choisie car c'est une Epoch Unix, après 1900.
 */
const DATE_REFERENCE_HORAIRE = "1970-01-01";

/**
 * Convertit une date en minutes depuis minuit.
 * @param dateValue - Date ou chaîne horaire.
 * @returns Minutes écoulées depuis minuit (UTC).
 */
function minutesDepuisMinuit(dateValue: Date | string): number {
	const parsed = moment.utc(dateValue);

	return parsed.hour() * 60 + parsed.minute();
}

/**
 * Normalise une heure sur la date de référence.
 * @param dateValue - Date ou chaîne horaire.
 * @returns Date ancrée sur {@link DATE_REFERENCE_HORAIRE} avec la même heure/minute.
 */
function normaliserHeureSurJourReference(dateValue: Date | string): Date {
	const parsed = moment.utc(dateValue);

	return moment
		.utc(DATE_REFERENCE_HORAIRE)
		.hour(parsed.hour())
		.minute(parsed.minute())
		.second(0)
		.millisecond(0)
		.toDate();
}

/**
 * Vérifie si un horaire est défini.
 * @param dateValue - Date ou chaîne à tester.
 * @returns true si l'horaire est exploitable (valide, année >= 1900).
 */
function horaireTimeIsSet(dateValue: Date | string | null | undefined): boolean {
	if (dateValue == null || dateValue === "") {
		return false;
	}
	const parsed = moment.utc(dateValue);

	return parsed.isValid() && parsed.year() >= 1900;
}

/**
 * Calcule la durée en minutes entre deux heures.
 * @param debut - Heure de début.
 * @param fin - Heure de fin.
 * @returns Durée en minutes (négative si fin < début).
 */
function dureeMinutesCreneau(debut: Date | null | undefined, fin: Date | null | undefined): number {
	if (!horaireTimeIsSet(fin)) {
		return 0;
	}
	const finMinutes = minutesDepuisMinuit(fin as Date);
	const debutMinutes = horaireTimeIsSet(debut) ? minutesDepuisMinuit(debut as Date) : 0;

	return finMinutes - debutMinutes;
}

/**
 * Formate une durée en minutes en HH:mm.
 * @param minutes - Durée en minutes.
 * @returns Durée au format HH:mm.
 */
export function formatMinutesAsHHmm(minutes: number): string {
	const min = Math.max(0, minutes);

	return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

/**
 * Convertit des heures décimales en minutes.
 * @param heuresDecimales - Heures décimales (ex. 7.5).
 * @returns Minutes entières.
 */
export function decimalHeuresEnMinutes(heuresDecimales: number): number {
	const h = Math.floor(heuresDecimales);

	return h * 60 + Math.floor((heuresDecimales - h) * 60);
}

/**
 * Formate une date en HH:mm.
 * @param dateValue - Date à formater.
 * @returns Heure HH:mm ou chaîne vide.
 */
export function formatHeureAffichage(dateValue: Date | null | undefined): string {
	return horaireTimeIsSet(dateValue) ? moment.utc(dateValue as Date).format("HH:mm") : "";
}

/**
 * Formate une durée en HH:mm en durée lisible (ex. 7h30).
 * @param hhMm - Durée HH:mm.
 * @returns Durée lisible (ex. 7h30).
 */
export function formatDureeAffichage(hhMm: string): string {
	if (!hhMm) {
		return "";
	}
	const [h, m] = hhMm.split(":").map(v => parseInt(v, 10) || 0);

	return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
}

/**
 * Parse une heure en HH:mm en Date.
 * @param value - Valeur input type="time".
 * @returns Date ou null.
 */
export function parseHeureInput(value: string): Date | null {
	if (!value) {
		return null;
	}
	const parsed = moment.utc(value, "HH:mm", true);

	return parsed.isValid() ? normaliserHeureSurJourReference(parsed.toDate()) : null;
}

/**
 * Ordonne les journées d'un horaire hebdomadaire.
 * @param horaire - Horaire hebdomadaire.
 * @returns Journées ordonnées lundi → samedi.
 */
export function journeesHoraireHebdoOrdonnees(horaire: HoraireHebdoDto): HoraireJourneeDto[] {
	return JOURS_SEMAINE.map(j => horaire[j.propriete] ?? { Id: 0 });
}

/**
 * Vérifie si une journée possède un horaire saisi.
 * @param journee - La journée.
 * @returns true si au moins un créneau est complet.
 */
function journeePossedeHoraireSaisi(journee: HoraireJourneeDto | null | undefined): boolean {
	if (journee == null) {
		return false;
	}

	return CRENEAUX_JOURNEE.some(c =>
		horaireTimeIsSet(journee[c.champDebut]) && horaireTimeIsSet(journee[c.champFin])
	);
}

/**
 * Vérifie si un horaire hebdomadaire est renseigné.
 * @param horaire - Horaire hebdomadaire.
 * @returns true si au moins une journée est renseignée.
 */
export function horairesHebdoSontRenseignes(horaire: HoraireHebdoDto | null | undefined): boolean {
	if (horaire == null) {
		return false;
	}

	return journeesHoraireHebdoOrdonnees(horaire).some(j => journeePossedeHoraireSaisi(j));
}

/**
 * Retourne le libellé d'un jour.
 * @param indexJournee - Index du jour (0 = lundi).
 * @returns Libellé du jour.
 */
export function libelleJourIndex(indexJournee: number): string {
	return JOURS_SEMAINE[indexJournee]?.libelle ?? "jour";
}

/**
 * Calcule la durée totale d'une journée.
 * @param journee - La journée (peut être null).
 * @returns Durée totale en minutes.
 */
export function dureeMinutesJournee(journee: HoraireJourneeDto | null | undefined): number {
	if (journee == null) {
		return 0;
	}

	return CRENEAUX_JOURNEE
		.map(c => dureeMinutesCreneau(journee[c.champDebut], journee[c.champFin]))
		.reduce((s, m) => s + m, 0);
}

/**
 * Calcule le total hebdomadaire en minutes (somme de toutes les journées).
 * @param horaire - Horaire hebdomadaire.
 * @returns Total en minutes, 0 si horaire absent.
 */
export function totalMinutesHoraireHebdo(horaire: HoraireHebdoDto | null | undefined): number {
	if (horaire == null) {
		return 0;
	}

	return journeesHoraireHebdoOrdonnees(horaire)
		.reduce((somme, journee) => somme + dureeMinutesJournee(journee), 0);
}

/**
 * Calcule le total HH:mm d'une journée.
 * @param journee - La journée.
 * @param jourActif - Jour coché.
 * @returns Total HH:mm ou chaîne vide (nécessite début + fin sur chaque créneau compté).
 */
export function totalJourneeHhMm(journee: HoraireJourneeDto, jourActif: boolean): string {
	if (!jourActif) {
		return "";
	}

	let totalMinutes = 0;
	let creneauComplet = false;

	CRENEAUX_JOURNEE.forEach(c => {
		if (!horaireTimeIsSet(journee[c.champDebut]) || !horaireTimeIsSet(journee[c.champFin])) {
			return;
		}
		const minutes = dureeMinutesCreneau(journee[c.champDebut], journee[c.champFin]);
		if (minutes >= 0) {
			totalMinutes += minutes;
			creneauComplet = true;
		}
	});

	return creneauComplet ? formatMinutesAsHHmm(totalMinutes) : "";
}

/**
 * Calcule le total hebdomadaire HH:mm.
 * @param totauxJournaliers - Totaux par jour (HH:mm).
 * @param joursActifs - Jours cochés.
 * @returns Total hebdomadaire HH:mm.
 */
export function totalSemaineHhMm(totauxJournaliers: string[], joursActifs: boolean[]): string {
	const totalMinutes = totauxJournaliers.reduce((s, hhMm, i) => {
		return !joursActifs[i] || !hhMm ? s : s + parseHhMmEnMinutes(hhMm);
	}, 0);

	return formatMinutesAsHHmm(totalMinutes);
}

/**
 * Calcule le total hebdomadaire HH:mm (mode identique).
 * @param journeeModeIdentique - Journée modèle.
 * @param joursActifs - Jours cochés.
 * @returns Total hebdomadaire HH:mm (mode identique).
 */
export function totalSemaineModeIdentiqueHhMm(
	journeeModeIdentique: HoraireJourneeDto,
	joursActifs: boolean[]
): string {
	const totalUnJour = totalJourneeHhMm(journeeModeIdentique, true);
	const nbActifs = joursActifs.filter(Boolean).length;

	return totalUnJour && nbActifs > 0
		? formatMinutesAsHHmm(parseHhMmEnMinutes(totalUnJour) * nbActifs)
		: "";
}

/**
 * Infère les jours actifs depuis un horaire hebdomadaire.
 * @param horaire - Horaire hebdomadaire.
 * @returns Jours actifs (6 booléens), ou profil par défaut si vide.
 */
export function infererJoursActifsDepuisHoraire(horaire: HoraireHebdoDto | null | undefined): boolean[] {
	if (horaire == null) {
		return [...JOURS_ACTIFS_PAR_DEFAUT];
	}
	const joursActifs = journeesHoraireHebdoOrdonnees(horaire).map(j => journeePossedeHoraireSaisi(j));

	return joursActifs.some(Boolean) ? joursActifs : [...JOURS_ACTIFS_PAR_DEFAUT];
}

/**
 * Retourne la première journée active avec un horaire saisi.
 * @param journees - Journées ordonnées.
 * @param joursActifs - Jours cochés.
 * @returns Première journée active avec horaire saisi.
 */
export function premiereJourneeActiveAvecValeur(
	journees: HoraireJourneeDto[],
	joursActifs: boolean[]
): HoraireJourneeDto | undefined {
	return journees.find((j, i) => joursActifs[i] && journeePossedeHoraireSaisi(j));
}

/**
 * Retourne le libellé synthétique des horaires de présence.
 * @param horaire - Horaire hebdomadaire.
 * @returns Libellé synthétique (À renseigner / créneau commun / Personnalisé).
 */
export function libelleAffichageHorairesPresence(horaire: HoraireHebdoDto | null | undefined): string {
	if (!horairesHebdoSontRenseignes(horaire)) {
		return "À renseigner";
	}

	const signatures = journeesHoraireHebdoOrdonnees(horaire as HoraireHebdoDto)
		.filter(j => journeePossedeHoraireSaisi(j))
		.map(journee => {
			return CRENEAUX_JOURNEE
				.map(c => {
					const debut = formatHeureAffichage(journee[c.champDebut]).replace(":", "h");
					const fin = formatHeureAffichage(journee[c.champFin]).replace(":", "h");

					return debut && fin ? `${debut} - ${fin}` : "";
				})
				.filter(s => s.length > 0)
				.join(" / ");
		})
		.filter(s => s.length > 0);

	return signatures.every(s => s === signatures[0]) ? signatures[0] : "Personnalisé";
}

/**
 * Vide une journée d'horaires.
 * @param journee - Journée à vider (mutée).
 */
export function viderJourneeHoraire(journee: HoraireJourneeDto): void {
	CHAMPS_HORAIRE.forEach(champ => {
		journee[champ] = undefined;
	});
}

/**
 * Normalise les créneaux vides d'une journée.
 * @param journee - Journée à normaliser (mutée).
 */
export function normaliserCreneauxVidesJournee(journee: HoraireJourneeDto): void {
	CRENEAUX_JOURNEE.forEach(c => {
		if (!horaireTimeIsSet(journee[c.champDebut]) && !horaireTimeIsSet(journee[c.champFin])) {
			journee[c.champDebut] = undefined;
			journee[c.champFin] = undefined;
		}
	});
}

/**
 * Normalise un horaire hebdomadaire brut API.
 * @param horaireRecu - Horaire brut API.
 * @returns Copie normalisée (journées définies, dates parsées).
 */
export function normaliserHoraireHebdoRecu(horaireRecu: HoraireHebdoDto): HoraireHebdoDto {
	const copie: HoraireHebdoDto = JSON.parse(JSON.stringify(horaireRecu));

	JOURS_SEMAINE.forEach(j => {
		copie[j.propriete] = copie[j.propriete] ?? { Id: 0 };
		const journee = copie[j.propriete];
		CHAMPS_HORAIRE.forEach(champ => {
			journee[champ] = horaireTimeIsSet(journee[champ])
				? normaliserHeureSurJourReference(journee[champ] as Date | string)
				: undefined;
		});
	});

	return copie;
}

/**
 * Réinitialise un horaire hebdomadaire.
 * @param horaire - Horaire à réinitialiser.
 * @returns Horaire vide avec les mêmes Id.
 */
export function reinitialiserHoraireHebdo(horaire: HoraireHebdoDto): HoraireHebdoDto {
	const resultat = { Id: horaire.Id ?? 0 } as HoraireHebdoDto;

	JOURS_SEMAINE.forEach(j => {
		resultat[j.propriete] = { Id: horaire[j.propriete]?.Id ?? 0 };
	});

	return resultat;
}

/**
 * Valide une journée d'horaires.
 * @param journee - Journée à valider.
 * @param indexJournee - Index pour le libellé des messages.
 * @param prefixeChamp - Préfixe des clés de champs en erreur.
 * @returns Messages et clés en erreur.
 */
export function validerJourneeHoraire(
	journee: HoraireJourneeDto,
	indexJournee: number,
	prefixeChamp: string
): ErreursValidationJournee {
	const messages: string[] = [];
	const champsErreur: string[] = [];
	const libelleJour = libelleJourIndex(indexJournee);

	CRENEAUX_JOURNEE.forEach(c => {
		const debutSet = horaireTimeIsSet(journee[c.champDebut]);
		const finSet = horaireTimeIsSet(journee[c.champFin]);

		if (finSet && !debutSet) {
			messages.push(`${c.libelle} (${libelleJour}) : heure de début manquante.`);
			champsErreur.push(`${prefixeChamp}-${c.suffixeDebut}`);
		}
		if (debutSet && !finSet) {
			messages.push(`${c.libelle} (${libelleJour}) : heure de fin manquante.`);
			champsErreur.push(`${prefixeChamp}-${c.suffixeFin}`);
		}
	});

	const creneauxNegatifs = CRENEAUX_JOURNEE.filter(c =>
		dureeMinutesCreneau(journee[c.champDebut], journee[c.champFin]) < 0
	);
	if (creneauxNegatifs.length > 0) {
		messages.push(`${libelleJour} : l'heure de début doit être antérieure à l'heure de fin.`);
		creneauxNegatifs.forEach(c => {
			champsErreur.push(`${prefixeChamp}-${c.suffixeDebut}`, `${prefixeChamp}-${c.suffixeFin}`);
		});
	}

	const matin = CRENEAUX_JOURNEE[0];
	const aprem = CRENEAUX_JOURNEE[1];
	if (horaireTimeIsSet(journee[matin.champFin]) && horaireTimeIsSet(journee[aprem.champDebut])) {
		if (dureeMinutesCreneau(journee[matin.champFin], journee[aprem.champDebut]) < 0) {
			messages.push(`${libelleJour} : les tranches matin et après-midi se chevauchent.`);
			champsErreur.push(`${prefixeChamp}-matin-fin`, `${prefixeChamp}-aprem-debut`);
		}
	}

	return { messages, champsErreur };
}



// WEBPACK FOOTER //
// ./src/Helper/horaireHebdoHelper.ts